import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, User, Mail, Phone, MapPinned, Calendar, FileText, Camera, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        const token = localStorage.getItem('token');
        await axios.put(
          `${API}/profile`,
          { avatar: base64Image },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Profile photo updated!');
        fetchProfile();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/profile`,
        {
          city: editData.city,
          address: editData.address
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profile updated successfully!');
      setEditMode(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-stone-900 mb-4">Profile not found</h2>
          <Button onClick={() => navigate('/home')}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="glassmorphism border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-home-btn"
              onClick={() => navigate('/home')}
              variant="outline"
              size="icon"
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <MapPin className="w-8 h-8 text-orange-600" />
              <h1 className="text-2xl font-bold text-stone-900">Profile</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card data-testid="profile-card" className="mb-6">
          <CardHeader className="text-center">
            <div className="relative inline-block mx-auto mb-4">
              {profile.avatar ? (
                <img
                  src={profile.avatar}
                  alt={profile.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-orange-100"
                />
              ) : (
                <div className="w-24 h-24 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                  <User className="w-12 h-12 text-white" />
                </div>
              )}
              <button
                onClick={() => fileInputRef.current.click()}
                disabled={uploading}
                className="absolute bottom-0 right-0 w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white hover:bg-orange-700 transition-colors"
              >
                {uploading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Camera className="w-4 h-4" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
            <CardTitle className="text-3xl">{profile.name}</CardTitle>
            <CardDescription className="flex items-center justify-center gap-2">
              {profile.verified ? (
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  Verified
                </Badge>
              ) : (
                <Badge variant="outline">Not Verified</Badge>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl">
                <Mail className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="text-xs text-stone-500 font-medium">Email</p>
                  <p className="text-sm text-stone-900">{profile.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl">
                <Phone className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="text-xs text-stone-500 font-medium">Phone</p>
                  <p className="text-sm text-stone-900">{profile.phone}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl">
                <MapPinned className="w-5 h-5 text-orange-600" />
                <div className="flex-1">
                  <p className="text-xs text-stone-500 font-medium">City</p>
                  <p className="text-sm text-stone-900">{profile.city || 'Not specified'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl">
                <FileText className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="text-xs text-stone-500 font-medium">Total Posts</p>
                  <p className="text-sm text-stone-900">{profile.posts_count}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl md:col-span-2">
                <MapPin className="w-5 h-5 text-orange-600" />
                <div className="flex-1">
                  <p className="text-xs text-stone-500 font-medium">Address</p>
                  <p className="text-sm text-stone-900">{profile.address || 'Not specified'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl md:col-span-2">
                <Calendar className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="text-xs text-stone-500 font-medium">Member Since</p>
                  <p className="text-sm text-stone-900">
                    {new Date(profile.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Dialog open={editMode} onOpenChange={setEditMode}>
                <DialogTrigger asChild>
                  <Button className="w-full rounded-full" variant="outline">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Profile</DialogTitle>
                    <DialogDescription>
                      Update your profile information
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={editData.city || ''}
                        onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={editData.address || ''}
                        onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                        placeholder="Street, Area, Landmark"
                      />
                    </div>
                    <Button
                      onClick={handleUpdateProfile}
                      className="w-full rounded-full btn-primary"
                    >
                      Save Changes
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button
            data-testid="view-posts-btn"
            onClick={() => navigate('/dashboard')}
            className="rounded-full btn-primary"
          >
            View My Posts
          </Button>
        </div>
      </div>
    </div>
  );
}